import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, ArrowUp, CheckCircle } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import type { PurchaseFlow, ChatMessage, ToolCallInfo, AgentToken, PurchaseStage, RichBlock, QuickReply, ActivityEntry } from '@/types/purchaseFlow';
import { AgentTokenType, TOOL_BLOCK_MAPPING, CREATE_TOOLS } from '@/types/purchaseFlow';
import { sendChatMessage } from '@/services/mockPurchaseFlowService';
import RichBlockRenderer from './richblocks/RichBlockRenderer';

// Quick action button definitions per stage — labels are i18n keys, messages stay in English (agent input)
const STAGE_QUICK_ACTIONS: Record<PurchaseStage, { labelKey: string; message: string }[]> = {
  vendor_select: [
    { labelKey: 'quickActions.showVendors', message: 'Show me the vendor list' },
    { labelKey: 'quickActions.newVendor', message: 'I need to add a new vendor' },
  ],
  item_select: [
    { labelKey: 'quickActions.searchParts', message: 'Show me available parts' },
    { labelKey: 'quickActions.addVendorPart', message: 'Browse parts' },
    { labelKey: 'quickActions.doneAdding', message: "That's all the items I need" },
    { labelKey: 'quickActions.goBack', message: 'Go back to previous stage' },
  ],
  price_confirm: [
    { labelKey: 'quickActions.viewPriceTiers', message: 'Show price tiers for these items' },
    { labelKey: 'quickActions.compareVendors', message: 'Show other vendors who supply these parts' },
    { labelKey: 'quickActions.adjustQty', message: 'I want to adjust quantities' },
    { labelKey: 'quickActions.removeItem', message: 'I want to remove an item' },
    { labelKey: 'quickActions.confirmPrices', message: 'Prices confirmed, proceed to delivery' },
    { labelKey: 'quickActions.goBack', message: 'Go back to previous stage' },
  ],
  delivery_schedule: [
    { labelKey: 'quickActions.setDeliveryDate', message: 'Set delivery date' },
    { labelKey: 'quickActions.setAddress', message: 'Set delivery address' },
    { labelKey: 'quickActions.setPaymentTerms', message: 'Set payment terms' },
    { labelKey: 'quickActions.confirmDelivery', message: 'Delivery details confirmed, create PO' },
    { labelKey: 'quickActions.goBack', message: 'Go back to previous stage' },
  ],
  iqc_rule: [
    { labelKey: 'quickActions.fullInspection', message: 'Full inspection for all items' },
    { labelKey: 'quickActions.sampling', message: 'Use sampling inspection' },
    { labelKey: 'quickActions.waiveIQC', message: 'Waive inspection — trusted vendor' },
    { labelKey: 'quickActions.goBack', message: 'Go back to previous stage' },
  ],
  receiving: [
    { labelKey: 'quickActions.shipmentArrived', message: 'The shipment arrived today' },
    { labelKey: 'quickActions.checkPOStatus', message: 'Show me what was ordered vs received' },
  ],
  invoice_input: [
    { labelKey: 'quickActions.usePOData', message: 'Create invoice from PO data' },
    { labelKey: 'quickActions.enterManually', message: 'I will enter the invoice details manually' },
  ],
  ap_completion: [
    { labelKey: 'quickActions.bankTransferDone', message: 'Payment was made by bank transfer' },
    { labelKey: 'quickActions.showPaymentInfo', message: 'Show payment details and vendor bank info' },
  ],
  close: [
    { labelKey: 'quickActions.showSummary', message: 'Show me the flow summary' },
    { labelKey: 'quickActions.closeFlow', message: 'Everything looks good, close this flow' },
  ],
};

// Parse [choices: "A" | "B"] from agent text
function parseQuickReplies(text: string): { cleaned: string; replies: QuickReply[] } {
  const match = text.match(/\[choices:\s*(.+?)\]/);
  if (!match) return { cleaned: text, replies: [] };

  const raw = match[1];
  const replies: QuickReply[] = [];
  const parts = raw.split('|');
  for (const part of parts) {
    const trimmed = part.trim().replace(/^"|"$/g, '');
    if (trimmed) replies.push({ label: trimmed, value: trimmed });
  }

  const cleaned = text.replace(/\[choices:\s*.+?\]/, '').trim();
  return { cleaned, replies };
}

interface ChatPanelProps {
  className?: string;
  flow: PurchaseFlow | null;
  onFlowUpdated?: () => void;
  onToolCallsUpdated?: (entries: ActivityEntry[]) => void;
  onUpdateItems?: (updates: { part_code: string; quantity: number; unit_price: number }[], removals: string[]) => void;
  onUpdateFlowFields?: (fields: Record<string, unknown>) => void;
  onAddItem?: (item: { part_code: string; product_name: string; specification: string; quantity: number; unit_price: number; currency: string }) => void;
  /** Pre-loaded messages from a fixture (for UI preview) */
  preloadedMessages?: ChatMessage[] | null;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ className, flow, onFlowUpdated, onToolCallsUpdated, onUpdateItems, onUpdateFlowFields, onAddItem, preloadedMessages }) => {
  const { t } = useTranslation('purchaseflow');
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Load preloaded messages from fixture
  useEffect(() => {
    if (preloadedMessages && preloadedMessages.length > 0) {
      setMessages(preloadedMessages);
      msgIdRef.current = Math.max(...preloadedMessages.map(m => m.id)) + 1;
    }
  }, [preloadedMessages]);
  const [input, setInput] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const msgIdRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const activityRef = useRef<ActivityEntry[]>([]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send initial greeting when flow is created (skip if preloaded)
  useEffect(() => {
    if (flow && messages.length === 0 && !isResponding && !preloadedMessages) {
      handleSend('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow?.flow_id]);

  const handleSend = useCallback(async (text: string) => {
    if (!flow || isResponding) return;

    // Add user message (skip empty for initial greeting)
    if (text.trim()) {
      setMessages(prev => [...prev, {
        id: msgIdRef.current++,
        role: 'user',
        content: text.trim(),
      }]);
    }
    setInput('');
    setIsResponding(true);

    // Create streaming assistant message
    const assistantId = msgIdRef.current++;
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      toolCalls: [],
      richBlocks: [],
      isStreaming: true,
    }]);

    const currentToolCalls: ToolCallInfo[] = [];
    const currentRichBlocks: RichBlock[] = [];
    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      await sendChatMessage(
        flow.flow_id,
        text.trim(),
        (token: AgentToken) => {
          switch (token.type) {
            case AgentTokenType.TextDelta: {
              const t = (token.data?.text as string) || '';
              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, content: m.content + t } : m
              ));
              break;
            }

            case AgentTokenType.TextDone: {
              // Parse quick replies from final text
              setMessages(prev => prev.map(m => {
                if (m.id !== assistantId) return m;
                const { cleaned, replies } = parseQuickReplies(m.content);
                return { ...m, content: cleaned, quickReplies: replies.length > 0 ? replies : m.quickReplies };
              }));
              break;
            }

            case AgentTokenType.ToolStart: {
              const tc: ToolCallInfo = {
                toolId: (token.data?.tool_id as string) || `t-${Date.now()}`,
                toolName: (token.data?.tool_name as string) || '',
                done: false,
                timestamp: Date.now(),
              };
              currentToolCalls.push(tc);

              // Add to activity log
              activityRef.current = [...activityRef.current, {
                toolId: tc.toolId, toolName: tc.toolName, timestamp: Date.now(),
                done: false, isCreate: CREATE_TOOLS.has(tc.toolName),
              }];
              onToolCallsUpdated?.(activityRef.current);

              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, toolCalls: [...currentToolCalls] } : m
              ));
              break;
            }

            case AgentTokenType.ToolDone: {
              const toolId = (token.data?.tool_id as string) || '';
              const toolName = (token.data?.tool_name as string) || '';
              const resultStr = (token.data?.result as string) || '{}';

              // Update tool call
              const tc = currentToolCalls.find(t => t.toolId === toolId);
              if (tc) {
                tc.done = true;
                tc.result = resultStr;
              }

              // Update activity log
              activityRef.current = activityRef.current.map(a =>
                a.toolId === toolId ? { ...a, done: true, result: safeParseJSON(resultStr) ?? undefined } : a
              );
              onToolCallsUpdated?.(activityRef.current);

              // Create rich block if tool has a mapping
              const blockType = TOOL_BLOCK_MAPPING[toolName];
              if (blockType) {
                const parsed = safeParseJSON(resultStr);
                if (parsed) {
                  currentRichBlocks.push({ type: blockType, data: parsed, toolCallId: toolId });
                }
              }

              // Insert stage banner if stage was advanced
              if (toolName === 'psi_flow_advance_stage') {
                const result = safeParseJSON(resultStr);
                const nextStage = (result as Record<string, unknown>)?.stage as string;
                if (nextStage) {
                  const label = nextStage;
                  setMessages(prev => [...prev, {
                    id: msgIdRef.current++,
                    role: 'stage_banner',
                    content: label,
                  }]);
                }
              }

              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, toolCalls: [...currentToolCalls], richBlocks: [...currentRichBlocks] }
                  : m
              ));
              break;
            }

            case AgentTokenType.TurnComplete:
              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, isStreaming: false } : m
              ));
              onFlowUpdated?.();
              break;

            case AgentTokenType.Error:
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, content: m.content + `\n\n**Error:** ${token.data?.err || 'Unknown error'}`, isStreaming: false }
                  : m
              ));
              break;
          }
        },
        abortController.signal,
      );
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: m.content || `Failed to get response: ${(err as Error).message}`, isStreaming: false }
            : m
        ));
      }
    } finally {
      setIsResponding(false);
      abortRef.current = null;
    }
  }, [flow, isResponding, onFlowUpdated, onToolCallsUpdated]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isResponding) handleSend(input);
    }
  };

  const handleInput = () => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 128) + 'px';
    }
  };

  // Get quick actions: use last message's parsed replies, or fall back to stage defaults
  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant' && !m.isStreaming);
  const agentReplies = lastAssistant?.quickReplies;
  const stageActionDefs = flow?.current_stage ? STAGE_QUICK_ACTIONS[flow.current_stage] || [] : [];
  const quickActions = agentReplies && agentReplies.length > 0
    ? agentReplies.map(r => ({ label: r.label, message: r.value }))
    : stageActionDefs.map(a => ({ label: t(a.labelKey), message: a.message }));

  return (
    <div className={`flex flex-col h-full min-h-0 border-x border-gray-200 dark:border-gray-700 ${className}`}>
      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-3 space-y-3">
        {messages.map(msg => (
          <div key={msg.id}>
            {/* Stage Banner */}
            {msg.role === 'stage_banner' && (
              <div className="flex justify-center py-2">
                <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-700 dark:text-emerald-400">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>{t('stageComplete')} <strong>{t(`stages.${msg.content}`, { defaultValue: msg.content })}</strong></span>
                </div>
              </div>
            )}

            {/* Assistant Message */}
            {msg.role === 'assistant' && (
              <div className="flex gap-2 px-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  {/* Inline tool call indicators */}
                  {msg.toolCalls?.map(tc => (
                    <div key={tc.toolId} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className={`w-1.5 h-1.5 rounded-full ${tc.done ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                      <span className="font-medium">{t(`tools.${tc.toolName}`, { defaultValue: tc.toolName })}</span>
                    </div>
                  ))}

                  {/* Rich blocks (embedded UI) */}
                  {msg.richBlocks?.map((block, i) => (
                    <RichBlockRenderer key={`${block.toolCallId}-${i}`} block={block} onAction={handleSend} onUpdateItems={onUpdateItems} onUpdateFlowFields={onUpdateFlowFields} onAddItem={onAddItem} />
                  ))}

                  {/* Text content */}
                  {msg.content && (
                    <div className="rounded-2xl rounded-tl-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm px-4 py-2.5 text-sm">
                      <div className="prose prose-sm dark:prose-invert max-w-none break-words
                        prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5
                        prose-table:border-collapse prose-table:w-full prose-table:text-xs
                        prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:px-3 prose-th:py-1.5 prose-th:bg-gray-100 dark:prose-th:bg-gray-700 prose-th:text-left prose-th:font-semibold
                        prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:px-3 prose-td:py-1.5
                        prose-code:bg-gray-100 dark:prose-code:bg-gray-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
                      ">
                        <Markdown remarkPlugins={[remarkGfm]}>{msg.content}</Markdown>
                      </div>
                      {msg.isStreaming && (
                        <span className="inline-block w-1.5 h-4 bg-blue-500 animate-pulse rounded-sm ml-0.5" />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* User Message */}
            {msg.role === 'user' && (
              <div className="flex justify-end px-2">
                <div className="max-w-[75%]">
                  <div className="rounded-2xl rounded-br-md bg-blue-600 text-white px-4 py-2.5 text-sm whitespace-pre-wrap break-words">
                    {msg.content}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick action buttons */}
      {quickActions.length > 0 && !isResponding && (
        <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-800 px-3 py-2 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex flex-wrap gap-1.5">
            {quickActions.map((action) => (
              <button
                key={action.label}
                onClick={() => handleSend(action.message)}
                disabled={isResponding}
                className="px-3 py-1.5 text-xs font-medium rounded-full
                  border border-blue-200 dark:border-blue-800
                  text-blue-700 dark:text-blue-300
                  bg-white dark:bg-blue-900/20
                  hover:bg-blue-50 dark:hover:bg-blue-900/40
                  disabled:opacity-40 disabled:cursor-not-allowed
                  transition-colors whitespace-nowrap"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-3 py-3 bg-white dark:bg-gray-900">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            placeholder={isResponding ? t('chat.thinking') : t('chat.placeholder')}
            disabled={isResponding || !flow}
            className="flex-1 resize-none bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 max-h-32 overflow-y-auto disabled:opacity-50"
          />
          <button
            onClick={() => input.trim() && handleSend(input)}
            disabled={!input.trim() || isResponding || !flow}
            className="p-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

function safeParseJSON(str: string): Record<string, unknown> | null {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

export default ChatPanel;
